"use client";
import type {
    APIAchievements,
    APIAchievementStats,
    APIHistoricalRatings,
    APIAppEnv,
    APIPublicGamePreferences,
    APIUser,
    APIUserStats } from "@rosette/ts/src/business/api/api_schema.js";
import {
    toCompleteUser,
} from "@rosette/ts/src/business/api/api_schema.js";
import { featureFlags } from "@rosette/ts/src/business/featureFlags.js";
import { EnvProvider, useOptionalUser } from "@/components/env/EnvContext.js";
import { GameAPIProvider } from "@/components/api/GameAPIContext.js";
import Image from "next/image";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import { getRoleIcon } from "@/components/icon/getRoleIcon.js";
import { FadingDiv } from "@/components/layout/FadingDiv.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import Link from "next/link";
import { DisplayName } from "@rosette/web_components/src/user/DisplayName.js";
import type { TagProps } from "@/components/user/Tags.js";
import { ProfileBadges } from "@/components/user/ProfileBadges.js";
import { RecentGames } from "@/components/account/game/RecentGames.js";
import { Permission } from "@rosette/ts/src/business/role/Permission.js";
import { AccountAdminControls } from "@/app/(info)/u/[username]/AccountAdminControls.js";
import { AccountBasicStats } from "@/components/user/stats/AccountBasicStats.js";
import { UserRollDistribution } from "@rosette/web_components/src/analysis/UserRollDistribution.js";
import { HistoricalRatingGraph } from "@rosette/web_components/src/user/stats/HistoricalRatingGraph.js";
import { AchievementIconGrid } from "@/components/achievements/AchievementIconGrid.js";
import type { AchievementProgressMap } from "@/utils/achievements/achievementProgress.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";


interface AccountDetailsProps {
    username: string;
    displayUser: APIUser | null;
    displayUserPreferences: APIPublicGamePreferences | null;
    displayUserStats: APIUserStats | null;
    displayUserRatings: Record<string, number> | null;
    displayUserHistoricalRatings: APIHistoricalRatings | null;
    achievements: APIAchievements | null;
    achievementStats: Record<string, APIAchievementStats> | null;
    achievementProgress: AchievementProgressMap;
    badges: TagProps[];
}


function AccountDetails({
    username,
    displayUser,
    displayUserPreferences,
    displayUserStats,
    displayUserRatings,
    displayUserHistoricalRatings,
    achievements,
    achievementStats,
    achievementProgress,
    badges,
}: AccountDetailsProps) {
    if (!displayUser)
        return (
            <div>
                <h1>
                    Unknown User
                </h1>
                <Panel>
                    <p className="py-4">
                        {`Unable to find a user with the username "${username}".`}
                    </p>
                </Panel>
            </div>
        );

    const roles = Role.list(displayUser);
    const role = roles[roles.length - 1];
    const roleIcon = (
        <Image className="mr-3 size-[5em] align-middle" src={getRoleIcon(role)} alt={role.getDesc()} title={role.getDesc()} />
    );
    return (
        <FadingDiv>
            <div className={cn(
                "mt-4 flex flex-row items-center py-4",
            )}>
                {role === Role.STANDARD && (
                    <Link href="/premium">
                        {roleIcon}
                    </Link>
                )}
                {role !== Role.STANDARD && roleIcon}

                <div>
                    <h1 className="m-0">
                        <DisplayName
                            name={displayUser.displayName}
                            preferences={displayUserPreferences}
                            roles={roles} />
                    </h1>
                    <p className="m-0 text-body">
                        {displayUser.username}
                    </p>
                    <ProfileBadges badges={badges} />
                </div>
            </div>

            {displayUserRatings && displayUserHistoricalRatings && (
                <HistoricalRatingGraph
                    latestRatings={displayUserRatings}
                    ratings={displayUserHistoricalRatings} />
            )}

            {displayUserStats && (
                <AccountBasicStats
                    stats={displayUserStats} />
            )}

            {featureFlags.achievementsEnabled && (
                <section className="mt-9">
                    <h2>Achievements</h2>
                    <AchievementIconGrid
                        achievements={achievements}
                        achievementStats={achievementStats}
                        achievementProgress={achievementProgress} />
                </section>
            )}

            <RecentGames user={displayUser} />

            {displayUserStats && (
                <>
                    <h2>Statistics</h2>
                    <Panel>
                        <ul className="list-disc space-y-4 pl-[1.4em] text-base leading-[1.4]">
                            <li>
                                {`Made ${displayUserStats.moves.toLocaleString()} moves`}
                                {` over ${displayUserStats.games} games`}
                            </li>
                            <li>
                                {`Captured ${displayUserStats.captures.toLocaleString()} pieces`}
                            </li>
                            <li>
                                {`Scored ${displayUserStats.scored_pieces.toLocaleString()} pieces`}
                            </li>
                            <li>
                                {`Landed on ${displayUserStats.lands_on_rosettes.toLocaleString()} rosettes`}
                            </li>
                            <li>
                                {`Rolled the dice ${displayUserStats.roll_4d2_count.toLocaleString()} times`}
                            </li>
                            {displayUserStats.longest_streak >= 4 && (
                                <li>
                                    {`Had a streak of ${displayUserStats.longest_streak.toLocaleString()} in `}
                                    <Link href={`/g/${displayUserStats.longest_streak_game}`}>
                                        this game
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </Panel>

                    <h2>Rolls</h2>
                    <UserRollDistribution stats={displayUserStats} />
                </>
            )}
        </FadingDiv>
    );
}


interface AccountProps {
    appEnv: APIAppEnv;
    username: string;
    displayUser: APIUser | null;
    displayUserPreferences: APIPublicGamePreferences | null;
    displayUserStats: APIUserStats | null;
    displayUserRatings: Record<string, number> | null;
    displayUserHistoricalRatings: APIHistoricalRatings | null;
    achievements: APIAchievements | null;
    achievementStats: Record<string, APIAchievementStats> | null;
    achievementProgress: AchievementProgressMap;
    badges: TagProps[];
}


function AccountInternal({
    username,
    displayUser,
    displayUserPreferences,
    displayUserStats,
    displayUserRatings,
    displayUserHistoricalRatings,
    achievements,
    achievementStats,
    achievementProgress,
    badges,
}: Omit<AccountProps, "appEnv">) {
    const ownUser = useOptionalUser();
    const ownCompleteUser = toCompleteUser(ownUser);
    const roles = Role.list(ownCompleteUser);
    return (
        <>
            <AccountDetails
                username={username}
                displayUser={displayUser}
                displayUserPreferences={displayUserPreferences}
                displayUserStats={displayUserStats}
                displayUserRatings={displayUserRatings}
                displayUserHistoricalRatings={displayUserHistoricalRatings}
                achievements={achievements}
                achievementStats={achievementStats}
                achievementProgress={achievementProgress}
                badges={badges} />

            {displayUser && Permission.ADMIN_USER_MANAGEMENT.hasPermission(roles) && (
                <AccountAdminControls
                    user={displayUser}
                    achievements={achievements} />
            )}
        </>
    );
}

export function Account({
    appEnv,
    username,
    displayUser,
    displayUserPreferences,
    displayUserStats,
    displayUserRatings,
    displayUserHistoricalRatings,
    achievements,
    achievementStats,
    achievementProgress,
    badges,
}: AccountProps) {
    return (
        <EnvProvider appEnv={appEnv}>
            <GameAPIProvider>
                <AccountInternal
                    username={username}
                    displayUser={displayUser}
                    displayUserPreferences={displayUserPreferences}
                    displayUserStats={displayUserStats}
                    displayUserRatings={displayUserRatings}
                    displayUserHistoricalRatings={displayUserHistoricalRatings}
                    achievements={achievements}
                    achievementStats={achievementStats}
                    achievementProgress={achievementProgress}
                    badges={badges} />
            </GameAPIProvider>
        </EnvProvider>
    );
}
